import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  Linkedin,
  MapPin,
  ShieldOff,
  UserCheck,
  Building2,
} from "lucide-react";
import { openExternal } from "@/lib/utils";
import type { ContactDetailFull } from "@/hooks/useContactDetail";

interface ContactHeaderProps {
  contact: ContactDetailFull | null;
  fallbackData?: {
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    job_title?: string | null;
    company_name?: string | null;
    email?: string | null;
    linkedin_url?: string | null;
    location?: string | null;
  } | null;
  isDnc?: boolean;
  dncReason?: string | null;
  isConnected?: boolean;
  connectedBy?: string | null;
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function ContactHeader({
  contact,
  fallbackData,
  isDnc,
  dncReason,
  isConnected,
  connectedBy,
}: ContactHeaderProps) {
  const displayName =
    contact?.full_name ||
    fallbackData?.full_name ||
    [fallbackData?.first_name, fallbackData?.last_name].filter(Boolean).join(" ") ||
    "Contact";
  const displayTitle = contact?.job_title || fallbackData?.job_title;
  const displayCompany = contact?.company_name || fallbackData?.company_name;
  const displayEmail = contact?.email || fallbackData?.email;
  const displayLinkedIn = contact?.linkedin_url || fallbackData?.linkedin_url;
  const displayLocation = contact?.location || contact?.clay_location || fallbackData?.location;

  return (
    <div className="px-6 pt-6 pb-4 border-b space-y-3 shrink-0">
      <div className="flex items-start gap-3">
        <Avatar className="h-12 w-12 shrink-0">
          {contact?.clay_picture_url && (
            <AvatarImage src={contact.clay_picture_url} alt={displayName} />
          )}
          <AvatarFallback className="text-sm bg-primary/10 text-primary">
            {getInitials(displayName)}
          </AvatarFallback>
        </Avatar>

        <div className="space-y-1 min-w-0 flex-1">
          <h2 className="text-xl font-semibold truncate">{displayName}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {displayTitle && (
              <span className="text-sm text-muted-foreground">{displayTitle}</span>
            )}
            {displayCompany && (
              <>
                <span className="text-sm text-muted-foreground">at</span>
                {contact?.company_id ? (
                  <Link
                    to={`/accounts/${contact.company_id}`}
                    className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Building2 className="h-3 w-3" />
                    {displayCompany}
                  </Link>
                ) : (
                  <span className="text-sm font-medium">{displayCompany}</span>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {contact?.carr_seniority && (
              <Badge variant="outline" className="text-xs">
                {contact.carr_seniority}
              </Badge>
            )}
            {contact?.carr_department && (
              <Badge variant="secondary" className="text-xs">
                {contact.carr_department}
              </Badge>
            )}
            {contact?.marketing_persona && (
              <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">
                {contact.marketing_persona}
              </Badge>
            )}
            {contact?.clay_enrichment_status === "enriched" && (
              <Badge
                variant="outline"
                className="text-[11px] bg-green-500/10 text-green-700 border-green-500/20"
              >
                Clay Enriched
              </Badge>
            )}
            {isDnc && (
              <Badge variant="destructive" className="text-[11px] gap-1">
                <ShieldOff className="h-3 w-3" />
                DNC{dncReason ? `: ${dncReason}` : ""}
              </Badge>
            )}
            {isConnected && (
              <Badge variant="outline" className="text-[11px] bg-blue-50 text-blue-700 border-blue-200 gap-1">
                <UserCheck className="h-3 w-3" />
                Connected{connectedBy ? ` (${connectedBy})` : ""}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-3 pt-1">
            {displayEmail && (
              <a
                href={`mailto:${displayEmail}`}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"
                onClick={(e) => e.stopPropagation()}
              >
                <Mail className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{displayEmail}</span>
              </a>
            )}
            {displayLinkedIn && (
              <button
                type="button"
                onClick={() => openExternal(displayLinkedIn)}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"
              >
                <Linkedin className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">LinkedIn</span>
              </button>
            )}
            {displayLocation && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {displayLocation}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
